// src/components/divesitedashboard/AddDivesiteDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Autocomplete,
  useTheme,
  useMediaQuery,
  IconButton,
  Typography,
  Paper,
  alpha,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// Glassmorphic Dialog Paper
const GlassDialogPaper = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 20,
  position: "relative",
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.06)}`,

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.4)}, 
      ${alpha(theme.palette.secondary.main, 0.3)}, 
      transparent)`,
    borderRadius: "20px 20px 0 0",
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 20%, 
      ${alpha(theme.palette.primary.main, 0.03)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 80%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  [theme.breakpoints.down("sm")]: {
    borderRadius: 16,
  },

  // Dark mode enhancements
  ...(theme.palette.mode === "dark" && {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.default, 0.8)} 0%, 
      ${alpha(theme.palette.background.default, 0.6)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,

    "&::before": {
      background: `linear-gradient(90deg, 
        transparent, 
        ${alpha(theme.palette.primary.main, 0.3)}, 
        ${alpha(theme.palette.secondary.main, 0.25)}, 
        transparent)`,
    },

    "&::after": {
      background: `radial-gradient(circle at 20% 20%, 
        ${alpha(theme.palette.primary.main, 0.02)} 0%, 
        transparent 50%), 
        radial-gradient(circle at 80% 80%, 
        ${alpha(theme.palette.secondary.main, 0.02)} 0%, 
        transparent 50%)`,
    },
  }),
}));

// Glassmorphic TextField
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.6)} 0%, 
      ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
    backdropFilter: "blur(10px) saturate(1.1)",
    WebkitBackdropFilter: "blur(10px) saturate(1.1)",
    border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
    borderRadius: 12,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

    "&:hover": {
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.7)} 0%, 
        ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },

    "&.Mui-focused": {
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.8)} 0%, 
        ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
      borderColor: alpha(theme.palette.primary.main, 0.5),
      boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
    },

    "& fieldset": {
      border: "none",
    },
  },

  "& .MuiInputLabel-root": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.9)} 0%, 
      ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    borderRadius: 8,
    padding: theme.spacing(0, 1),

    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },

  ...(theme.palette.mode === "dark" && {
    "& .MuiOutlinedInput-root": {
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.default, 0.6)} 0%, 
        ${alpha(theme.palette.background.default, 0.4)} 100%)`,
      border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,

      "&:hover": {
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.background.default, 0.7)} 0%, 
          ${alpha(theme.palette.background.default, 0.5)} 100%)`,
      },

      "&.Mui-focused": {
        background: `linear-gradient(135deg, 
          ${alpha(theme.palette.background.default, 0.8)} 0%, 
          ${alpha(theme.palette.background.default, 0.6)} 100%)`,
      },
    },

    "& .MuiInputLabel-root": {
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.default, 0.9)} 0%, 
        ${alpha(theme.palette.background.default, 0.7)} 100%)`,
    },
  }),
}));

// Glassmorphic Button
const GlassButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.8)} 0%, 
    ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  borderRadius: 12,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.08)} 0%, 
      ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&:hover": {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.paper, 0.9)} 0%, 
      ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
    borderColor: alpha(theme.palette.primary.main, 0.2),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,

    "&::before": {
      opacity: 1,
    },
  },

  "&:active": {
    transform: "translateY(0px)",
  },

  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    transition: "transform 0.3s ease",
  },

  "&:hover .MuiButton-startIcon, &:hover .MuiButton-endIcon": {
    transform: "scale(1.1)",
  },

  ...(theme.palette.mode === "dark" && {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.default, 0.8)} 0%, 
      ${alpha(theme.palette.background.default, 0.6)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,

    "&:hover": {
      background: `linear-gradient(135deg, 
        ${alpha(theme.palette.background.default, 0.9)} 0%, 
        ${alpha(theme.palette.background.default, 0.7)} 100%)`,
    },
  }),
}));

// Glassmorphic Option Box
const GlassOptionBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.6)} 0%, 
    ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
  backdropFilter: "blur(10px) saturate(1.1)",
  WebkitBackdropFilter: "blur(10px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
  borderRadius: 8,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, 0.05)} 0%, 
      ${alpha(theme.palette.secondary.main, 0.03)} 100%)`,
    opacity: 0,
    transition: "opacity 0.3s ease",
  },

  "&:hover::before": {
    opacity: 1,
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },

  ...(theme.palette.mode === "dark" && {
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.background.default, 0.6)} 0%, 
      ${alpha(theme.palette.background.default, 0.4)} 100%)`,
  }),
}));

const AddDivesiteDialog = ({
  open,
  onClose,
  divesites,
  onAddDivesite,
  loading,
  showOnlyNonDashboard,
  dashboardDivesiteIds,
}) => {
  console.log("AddDivesiteDialog - Received divesites prop:", divesites);

  const [selectedDivesite, setSelectedDivesite] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);

  // Mobile-friendly responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    console.log(
      "AddDivesiteDialog - useEffect: Filtering autocomplete options."
    );
    console.log(" - showOnlyNonDashboard:", showOnlyNonDashboard);
    console.log(" - dashboardDivesiteIds:", dashboardDivesiteIds);
    console.log(" - inputValue:", inputValue);
    console.log(
      " - all divesites:",
      divesites ? divesites.map((d) => d?.uddf?.divesite?.site?.name) : []
    );

    const availableDivesites = showOnlyNonDashboard
      ? divesites.filter((site) => !dashboardDivesiteIds.includes(site._id))
      : divesites;

    console.log(
      " - availableDivesites:",
      availableDivesites
        ? availableDivesites.map((d) => d?.uddf?.divesite?.site?.name)
        : []
    );

    const filteredOptions = availableDivesites.filter((site) =>
      site?.uddf?.divesite?.site?.name
        ?.toLowerCase()
        ?.includes(inputValue.toLowerCase())
    );

    console.log(
      " - filteredOptions:",
      filteredOptions.map((d) => d?.uddf?.divesite?.site?.name)
    );

    setAutocompleteOptions(filteredOptions);
  }, [divesites, inputValue, showOnlyNonDashboard, dashboardDivesiteIds]);

  const handleAutocompleteChange = (event, newValue) => {
    console.log(
      "AddDivesiteDialog - handleAutocompleteChange: Selected value:",
      newValue ? newValue?.uddf?.divesite?.site?.name : null
    );
    setSelectedDivesite(newValue);
    if (newValue) {
      onAddDivesite(newValue);
      onClose();
    }
  };

  const handleInputChange = (event, newInputValue) => {
    console.log(
      "AddDivesiteDialog - handleInputChange: Input value changed to:",
      newInputValue
    );
    setInputValue(newInputValue);
  };

  // Mobile-optimized dialog props
  const getDialogProps = () => {
    if (isMobile) {
      return {
        fullScreen: true,
        maxWidth: false,
        PaperComponent: GlassDialogPaper,
        PaperProps: {
          sx: {
            margin: 0,
            width: "100%",
            height: "100%",
            borderRadius: 0,
          },
        },
      };
    }

    return {
      fullWidth: true,
      maxWidth: isTablet ? "md" : "sm",
      PaperComponent: GlassDialogPaper,
      PaperProps: {
        sx: {
          margin: isTablet ? theme.spacing(2) : theme.spacing(4),
          width: isTablet ? "calc(100% - 32px)" : "auto",
        },
      },
    };
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      {...getDialogProps()}
      aria-labelledby="add-divesite-dialog-title"
    >
      <DialogTitle
        id="add-divesite-dialog-title"
        sx={{
          pb: 1,
          pr: isMobile ? 6 : 3,
          fontSize: isMobile ? "1.1rem" : "1.25rem",
          fontWeight: 600,
          position: "relative",
          zIndex: 2,
        }}
      >
        {isMobile ? (
          <Box>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontSize: "1.1rem", fontWeight: 600 }}
            >
              Add Divesite
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mt: 0.5, fontSize: "0.875rem" }}
            >
              From your favorites to Dashboard
            </Typography>
          </Box>
        ) : (
          "Add Divesite from your favorites to Dashboard"
        )}

        {isMobile && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
              background: `linear-gradient(135deg, 
                ${alpha(theme.palette.background.paper, 0.8)} 0%, 
                ${alpha(theme.palette.background.paper, 0.6)} 100%)`,
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
              borderRadius: "50%",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "&:hover": {
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.background.paper, 0.9)} 0%, 
                  ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
                transform: "scale(1.05)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        dividers={!isMobile}
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 2,
          flex: isMobile ? 1 : "none",
          position: "relative",
          zIndex: 2,
        }}
      >
        <Box sx={{ mb: isMobile ? 1 : 2 }}>
          <Autocomplete
            id="divesite-autocomplete"
            fullWidth
            loading={loading}
            options={autocompleteOptions}
            getOptionLabel={(option) =>
              option?.uddf?.divesite?.site?.name || ""
            }
            value={selectedDivesite}
            onChange={handleAutocompleteChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            renderInput={(params) => (
              <GlassTextField
                {...params}
                label="Divesite"
                placeholder={
                  isMobile
                    ? "Search divesites..."
                    : "Select a divesite from your favorites"
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
                size={isMobile ? "medium" : "small"}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: isMobile ? "56px" : "40px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: isMobile ? "1rem" : "0.875rem",
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li
                {...props}
                key={option?._id}
                style={{
                  ...props.style,
                  minHeight: isMobile ? "48px" : "40px",
                  padding: 0,
                  margin: theme.spacing(0.5),
                  borderRadius: 8,
                }}
              >
                <GlassOptionBox
                  sx={{
                    width: "100%",
                    p: isMobile ? "12px 16px" : "8px 16px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: isMobile ? "1rem" : "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    {option?.uddf?.divesite?.site?.name}
                  </Typography>
                  {option?.uddf?.divesite?.site?.geography?.location && (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: isMobile ? "0.75rem" : "0.6875rem",
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      {option.uddf.divesite.site.geography.location}
                    </Typography>
                  )}
                </GlassOptionBox>
              </li>
            )}
            noOptionsText={
              loading
                ? "Loading divesites..."
                : inputValue && autocompleteOptions.length === 0
                ? isMobile
                  ? `No matches for "${
                      inputValue.length > 15
                        ? inputValue.substring(0, 15) + "..."
                        : inputValue
                    }"`
                  : `No divesites found matching "${inputValue}" ${
                      showOnlyNonDashboard
                        ? "(showing only non-dashboard divesites)"
                        : ""
                    }`
                : "No divesites available"
            }
            ListboxProps={{
              sx: {
                maxHeight: isMobile ? "60vh" : "40vh",
                background: `linear-gradient(135deg, 
                  ${alpha(theme.palette.background.paper, 0.9)} 0%, 
                  ${alpha(theme.palette.background.paper, 0.7)} 100%)`,
                backdropFilter: "blur(20px) saturate(1.1)",
                WebkitBackdropFilter: "blur(20px) saturate(1.1)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
                borderRadius: 12,
                padding: theme.spacing(0.5),
                ...(theme.palette.mode === "dark" && {
                  background: `linear-gradient(135deg, 
                    ${alpha(theme.palette.background.default, 0.9)} 0%, 
                    ${alpha(theme.palette.background.default, 0.7)} 100%)`,
                  border: `1px solid ${alpha(
                    theme.palette.common.white,
                    0.08
                  )}`,
                }),
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 2 : 1.5,
          justifyContent: isMobile ? "stretch" : "flex-end",
          gap: isMobile ? 1 : 0,
          position: "relative",
          zIndex: 2,
        }}
      >
        <GlassButton
          onClick={onClose}
          color="primary"
          size={isMobile ? "large" : "medium"}
          sx={{
            minHeight: isMobile ? "48px" : "36px",
            flex: isMobile ? 1 : "none",
            fontSize: isMobile ? "1rem" : "0.875rem",
          }}
        >
          Cancel
        </GlassButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddDivesiteDialog;
